/**
 * Shipping Cart Tier Sync for Happy Angler - V3.3
 * ==============================================
 *
 * MUUTOKSET V3.2 → V3.3:
 * - KORJAUS (Bogos Bundle): Rivit joilta item-tason shipping-propertyt
 *   puuttuvat rikastetaan Section Rendering API:lla ennen aggregointia
 *   (GET {tuote-url}?section_id=shipping-class-data).
 *   Bogos lisää/uudelleenkirjoittaa rivit omilla propertyillaan, jolloin
 *   _priority_*_tier ym. katoavat — ilman rikastusta koko korin tieriksi
 *   tuli "0", joka ei vastaa yhtään Packrooster-sääntöä → kassalla ei
 *   tarjottu toimitustapoja.
 * - KORJAUS: computeMaxTier ohittaa puuttuvat/virheelliset tier-arvot
 *   eikä laske niitä nollaksi → jos millään rivillä ei ole tier-dataa,
 *   palautuu oletus "1" (ei "0").
 */

(function() {
  'use strict';

  // ============================================================
  // KONFIGURAATIO
  // ============================================================
  
  // Tier-propertyt: tuotekohtainen -> cart-tason aggregoitu
  const TIER_PROPERTY_MAP = [
    // Priority Parcel
    { itemProp: '_priority_us_tier', cartProp: '_priority_us_cart_tier' },
    { itemProp: '_priority_kv3_tier', cartProp: '_priority_kv3_cart_tier' },
    { itemProp: '_priority_ba_il_tier', cartProp: '_priority_ba_il_cart_tier' },
    { itemProp: '_priority_kv4_au_tier', cartProp: '_priority_kv4_au_cart_tier' },
    { itemProp: '_priority_kv2_tier', cartProp: '_priority_kv2_cart_tier' },
    { itemProp: '_priority_gb_tier', cartProp: '_priority_gb_cart_tier' },
    { itemProp: '_priority_dk_tier', cartProp: '_priority_dk_cart_tier' },
    { itemProp: '_priority_am_tm_tier', cartProp: '_priority_am_tm_cart_tier' },
    { itemProp: '_priority_jp_tier', cartProp: '_priority_jp_cart_tier' },
    { itemProp: '_priority_ge_tier', cartProp: '_priority_ge_cart_tier' },
    // Parcel Connect
    { itemProp: '_parcel_connect_tier', cartProp: '_parcel_connect_cart_tier' },
    // UPS
    { itemProp: '_ups_gb_tier', cartProp: '_ups_gb_cart_tier' },
    { itemProp: '_ups_de_tier', cartProp: '_ups_de_cart_tier' },
    // Baltia
    { itemProp: '_baltia_tier', cartProp: '_baltia_cart_tier' },
  ];

  // Boolean-propertyt: käytetään "strictest wins" -logiikkaa
  // false voittaa true:n (jos yksikin tuote ei salli, kukaan ei salli)
  const BOOLEAN_PROPERTY_MAP = [
    // Suomi
    { prop: '_allows_small_parcel', cartProp: '_allows_small_parcel_cart' },
    { prop: '_allows_small_parcel_box', cartProp: '_allows_small_parcel_box_cart' },
    { prop: '_allows_postal_parcel', cartProp: '_allows_postal_parcel_cart' },
    { prop: '_allows_automat', cartProp: '_allows_automat_cart' },
    { prop: '_allows_home_parcel', cartProp: '_allows_home_parcel_cart' },
    { prop: '_allows_express', cartProp: '_allows_express_cart' },
    { prop: '_allows_schenker', cartProp: '_allows_schenker_cart' },
    // Ruotsi
    { prop: '_allows_pickup_se', cartProp: '_allows_pickup_se_cart' },
    { prop: '_allows_home_se', cartProp: '_allows_home_se_cart' },
    // Tanska
    { prop: '_allows_pickup_dk', cartProp: '_allows_pickup_dk_cart' },
    { prop: '_allows_home_dk', cartProp: '_allows_home_dk_cart' },
  ];

  // "Needs" propertyt: true voittaa false:n
  const NEEDS_PROPERTY_MAP = [
    { prop: '_needs_postal_bulky', cartProp: '_needs_postal_bulky_cart' },
    { prop: '_needs_home_bulky', cartProp: '_needs_home_bulky_cart' },
    { prop: '_needs_express_bulky', cartProp: '_needs_express_bulky_cart' },
    { prop: '_allows_pickup_bulky_se', cartProp: '_allows_pickup_bulky_se_cart' },
    { prop: '_requires_freight', cartProp: '_requires_freight_cart' },
  ];

  // Kaikki item-tason propertyt (tier + boolean + needs)
  const ALL_ITEM_PROPERTIES = [
    '_priority_us_tier', '_priority_kv3_tier', '_priority_ba_il_tier', 
    '_priority_kv4_au_tier', '_priority_kv2_tier', '_priority_gb_tier',
    '_priority_dk_tier', '_priority_am_tm_tier', '_priority_jp_tier',
    '_priority_ge_tier', '_parcel_connect_tier', '_ups_gb_tier',
    '_ups_de_tier', '_baltia_tier',
    '_allows_small_parcel', '_allows_small_parcel_box', '_allows_postal_parcel',
    '_allows_automat', '_allows_home_parcel', '_allows_express', '_allows_schenker',
    '_allows_pickup_se', '_allows_home_se', '_allows_pickup_dk', '_allows_home_dk',
    '_needs_postal_bulky', '_needs_home_bulky', '_needs_express_bulky',
    '_allows_pickup_bulky_se', '_requires_freight'
  ];

  // ============================================================
  // APUFUNKTIOT
  // ============================================================

  let syncInFlight = null;

  /**
   * Hakee ostoskorin
   */
  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  /**
   * Tarkistaa onko arvo "none"
   */
  function isNone(value) {
    return value === 'none' || value === 'None' || value === 'NONE';
  }

  /**
   * Muuntaa tier-arvon numeroksi (none -> null, jotta voidaan erottaa)
   */
  function toTierNumber(value) {
    if (isNone(value)) {
      return null; // Palautetaan null "none":lle
    }
    // V3.3: Puuttuva/virheellinen arvo ohitetaan MAX-laskennassa
    // (aiemmin 0, jolloin kori jonka kaikilta riveiltä puuttui tier-data
    // sai tierin "0" — tier "0" ei vastaa yhtään Packrooster-sääntöä)
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    const num = parseInt(String(value), 10);
    return Number.isFinite(num) ? num : undefined;
  }

  /**
   * Muuntaa boolean-arvon (string tai boolean)
   */
  function toBoolean(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return false;
  }

  /**
   * Laskee MAX tierin koko ostoskorista
   * V3 KORJAUS: Jos MIKÄ TAHANSA tuote on "none", palauttaa "none"
   */
  function computeMaxTier(cart, itemProp) {
    const tiers = [];
    
    for (const item of cart.items || []) {
      const props = item.properties || {};
      const value = props[itemProp];
      
      // Jos arvo on "none", koko kori on "none" tälle palvelulle
      if (isNone(value)) {
        return 'none';
      }
      
      tiers.push(toTierNumber(value));
    }
    
    // Filtteröi pois null-arvot (ei pitäisi olla koska none palautettiin jo)
    const validTiers = tiers.filter(t => t !== null && t !== undefined);
    
    if (validTiers.length === 0) {
      return '1'; // Oletus tier 1 jos ei dataa
    }
    
    // Palauta MAX tier numerona
    return String(Math.max(...validTiers));
  }

  /**
   * Laskee "strictest" boolean-arvon (false voittaa)
   * Huom: Jos property puuttuu, oletetaan true (eli sallittu)
   */
  function computeStrictestBoolean(cart, prop) {
    let hasAnyValue = false;
    let result = true;
    
    for (const item of cart.items || []) {
      const props = item.properties || {};
      if (props[prop] !== undefined) {
        hasAnyValue = true;
        const val = toBoolean(props[prop]);
        if (!val) {
          result = false;
          break;
        }
      }
    }
    
    // Jos yksikään item ei sisällä propertya, palautetaan true (oletus)
    return hasAnyValue ? result : true;
  }

  /**
   * Laskee "any true" boolean-arvon (true voittaa)
   */
  function computeAnyTrue(cart, prop) {
    for (const item of cart.items || []) {
      const props = item.properties || {};
      if (props[prop] !== undefined && toBoolean(props[prop])) {
        return true;
      }
    }
    return false;
  }

  /**
   * Päivittää yhden line itemin propertyt
   */
  async function updateLineItemProperties(itemKey, newProps, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: itemKey,
        quantity,
        properties: newProps,
      }),
    });
    if (!response.ok) throw new Error('Failed to update line item properties');
    return response.json();
  }

  // ============================================================
  // VARIANT SHIPPING DATA (sivulta luettava)
  // ============================================================

  /**
   * Hae variantin shipping-propertyt sivulla olevasta datasta
   */
  function getVariantShippingData(variantId) {
    // Etsi shipping data sivulta
    const dataEl = document.querySelector('[data-shipping-classes]');
    if (!dataEl) return null;
    
    try {
      const allData = JSON.parse(dataEl.textContent);
      return allData[variantId] || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Luo shipping propertyt objektina variantin datasta
   */
  function createShippingPropertiesFromData(shippingData) {
    if (!shippingData) return null;
    
    const props = {};
    
    // Tier propertyt
    if (shippingData.priority_us_tier) props['_priority_us_tier'] = shippingData.priority_us_tier;
    if (shippingData.priority_kv3_tier) props['_priority_kv3_tier'] = shippingData.priority_kv3_tier;
    if (shippingData.priority_ba_il_tier) props['_priority_ba_il_tier'] = shippingData.priority_ba_il_tier;
    if (shippingData.priority_kv4_au_tier) props['_priority_kv4_au_tier'] = shippingData.priority_kv4_au_tier;
    if (shippingData.priority_kv2_tier) props['_priority_kv2_tier'] = shippingData.priority_kv2_tier;
    if (shippingData.priority_gb_tier) props['_priority_gb_tier'] = shippingData.priority_gb_tier;
    if (shippingData.priority_dk_tier) props['_priority_dk_tier'] = shippingData.priority_dk_tier;
    if (shippingData.priority_am_tm_tier) props['_priority_am_tm_tier'] = shippingData.priority_am_tm_tier;
    if (shippingData.priority_jp_tier) props['_priority_jp_tier'] = shippingData.priority_jp_tier;
    if (shippingData.priority_ge_tier) props['_priority_ge_tier'] = shippingData.priority_ge_tier;
    if (shippingData.parcel_connect_tier) props['_parcel_connect_tier'] = shippingData.parcel_connect_tier;
    if (shippingData.ups_gb_tier) props['_ups_gb_tier'] = shippingData.ups_gb_tier;
    if (shippingData.ups_de_tier) props['_ups_de_tier'] = shippingData.ups_de_tier;
    if (shippingData.baltia_tier) props['_baltia_tier'] = shippingData.baltia_tier;
    
    // Boolean propertyt
    if (shippingData.allows_small_parcel !== undefined) props['_allows_small_parcel'] = String(shippingData.allows_small_parcel);
    if (shippingData.allows_small_parcel_box !== undefined) props['_allows_small_parcel_box'] = String(shippingData.allows_small_parcel_box);
    if (shippingData.allows_postal_parcel !== undefined) props['_allows_postal_parcel'] = String(shippingData.allows_postal_parcel);
    if (shippingData.allows_automat !== undefined) props['_allows_automat'] = String(shippingData.allows_automat);
    if (shippingData.allows_home_parcel !== undefined) props['_allows_home_parcel'] = String(shippingData.allows_home_parcel);
    if (shippingData.allows_express !== undefined) props['_allows_express'] = String(shippingData.allows_express);
    if (shippingData.allows_schenker !== undefined) props['_allows_schenker'] = String(shippingData.allows_schenker);
    if (shippingData.allows_pickup_se !== undefined) props['_allows_pickup_se'] = String(shippingData.allows_pickup_se);
    if (shippingData.allows_home_se !== undefined) props['_allows_home_se'] = String(shippingData.allows_home_se);
    if (shippingData.allows_pickup_dk !== undefined) props['_allows_pickup_dk'] = String(shippingData.allows_pickup_dk);
    if (shippingData.allows_home_dk !== undefined) props['_allows_home_dk'] = String(shippingData.allows_home_dk);
    
    // Needs propertyt
    if (shippingData.needs_postal_bulky !== undefined) props['_needs_postal_bulky'] = String(shippingData.needs_postal_bulky);
    if (shippingData.needs_home_bulky !== undefined) props['_needs_home_bulky'] = String(shippingData.needs_home_bulky);
    if (shippingData.needs_express_bulky !== undefined) props['_needs_express_bulky'] = String(shippingData.needs_express_bulky);
    if (shippingData.allows_pickup_bulky_se !== undefined) props['_allows_pickup_bulky_se'] = String(shippingData.allows_pickup_bulky_se);
    if (shippingData.requires_freight !== undefined) props['_requires_freight'] = String(shippingData.requires_freight);
    
    return Object.keys(props).length > 0 ? props : null;
  }

  // ============================================================
  // V3.3: PUUTTUVIEN PROPERTYJEN RIKASTUS (Section Rendering API)
  // ============================================================

  // Sektio joka palauttaa tuotteen [data-shipping-classes] JSON:in
  const SHIPPING_DATA_SECTION_ID = 'shipping-class-data';

  // Välimuisti: handle -> varianttikohtainen shipping-data (tai null jos haku epäonnistui)
  const productShippingDataCache = new Map();

  /**
   * Hae tuotteen kaikkien varianttien shipping-data Section Rendering API:lla.
   * Käytetään kun korin riviltä puuttuvat item-tason propertyt
   * (esim. Bogos Bundle lisää rivit ilman niitä).
   */
  async function fetchProductShippingData(item) {
    const handle = item.handle;
    if (!handle) return null;

    if (productShippingDataCache.has(handle)) {
      return productShippingDataCache.get(handle);
    }

    let data = null;
    try {
      // item.url sisältää mahdollisen locale-prefiksin (esim. /sv/products/...)
      const baseUrl = item.url ? item.url.split('?')[0] : '/products/' + handle;
      const response = await fetch(baseUrl + '?section_id=' + SHIPPING_DATA_SECTION_ID, {
        credentials: 'same-origin',
      });
      if (response.ok) {
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const dataEl = doc.querySelector('[data-shipping-classes]');
        if (dataEl) {
          data = JSON.parse(dataEl.textContent);
        }
      }
    } catch (e) {
      data = null;
    }

    productShippingDataCache.set(handle, data);
    return data;
  }

  /**
   * Onko rivillä item-tason tier-propertyt?
   */
  function hasItemTierProperties(item) {
    const props = item.properties || {};
    return TIER_PROPERTY_MAP.some(m => props[m.itemProp] !== undefined && props[m.itemProp] !== '');
  }

  /**
   * Rikastaa rivit joilta item-tason shipping-propertyt puuttuvat.
   * Muokkaa cart-objektin items[].properties paikallaan (jotta aggregointi
   * näkee oikeat arvot) ja palauttaa rikastettujen rivien avaimet — ne
   * pitää kirjoittaa Shopifyyn vaikka cart-propertyt eivät muuttuisi.
   */
  async function enrichItemsMissingProperties(cart) {
    const enrichedKeys = new Set();

    for (const item of cart.items || []) {
      if (hasItemTierProperties(item)) continue;

      const productData = await fetchProductShippingData(item);
      const variantData = productData ? productData[String(item.variant_id)] : null;
      const shippingProps = createShippingPropertiesFromData(variantData);
      if (!shippingProps) continue;

      // Merge: muiden sovellusten propertyt (esim. _bogos_*) säilyvät
      item.properties = { ...(item.properties || {}), ...shippingProps };
      enrichedKeys.add(item.key);
    }

    return enrichedKeys;
  }

  /**
   * Tarkista onko payloadissa jo shipping propertyt
   */
  function hasShippingProperties(body) {
    if (!body) return false;
    
    try {
      let data;
      if (typeof body === 'string') {
        data = JSON.parse(body);
      } else if (body instanceof FormData) {
        // FormData - tarkista onko nimenomaan shipping-propertyja
        // V3.3: aiemmin matchasi mihin tahansa properties[_ -avaimeen,
        // jolloin esim. Bogosin properties[_bogos_trigger] esti
        // shipping-propertyjen lisäämisen
        for (const key of body.keys()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && ALL_ITEM_PROPERTIES.includes(match[1])) {
            return true;
          }
        }
        return false;
      } else {
        data = body;
      }
      
      // Tarkista JSON data
      const props = data.properties || {};
      return ALL_ITEM_PROPERTIES.some(p => props[p] !== undefined);
    } catch (e) {
      return false;
    }
  }

  /**
   * Poimi variant ID payloadista
   */
  function extractVariantIdFromPayload(body) {
    if (!body) return null;
    
    try {
      let data;
      if (typeof body === 'string') {
        data = JSON.parse(body);
      } else if (body instanceof FormData) {
        return body.get('id') || body.get('items[0][id]');
      } else {
        data = body;
      }
      
      // Yksittäinen tuote
      if (data.id) return String(data.id);
      
      // Items array
      if (data.items && data.items[0] && data.items[0].id) {
        return String(data.items[0].id);
      }
      
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Lisää propertyt JSON payloadiin
   */
  function addPropertiesToJsonPayload(bodyString, shippingProps) {
    try {
      const data = JSON.parse(bodyString);
      data.properties = { ...(data.properties || {}), ...shippingProps };
      return JSON.stringify(data);
    } catch (e) {
      return bodyString;
    }
  }

  /**
   * Lisää propertyt FormData payloadiin
   */
  function addPropertiesToFormData(formData, shippingProps) {
    for (const [key, value] of Object.entries(shippingProps)) {
      formData.append(`properties[${key}]`, value);
    }
  }

  // ============================================================
  // PÄÄLOGIIKKA
  // ============================================================

  /**
   * Synkronoi koko ostoskorin tier-propertyt
   */
  async function syncCartTiers() {
    // Varmista että vain yksi sync kerrallaan
    if (syncInFlight) {
      await syncInFlight;
    }

    syncInFlight = (async () => {
      try {
        const cart = await fetchCart();

        if (!cart.items || cart.items.length === 0) {
          return;
        }

        // V3.3: Rikasta rivit joilta item-tason propertyt puuttuvat
        // (esim. Bogos Bundle -rivit) ENNEN aggregointia
        const enrichedKeys = await enrichItemsMissingProperties(cart);

        // Laske aggregoidut arvot
        const aggregatedValues = {};

        // Tier-arvot: MAX voittaa, PAITSI "none" voittaa kaiken
        for (const mapping of TIER_PROPERTY_MAP) {
          const maxTier = computeMaxTier(cart, mapping.itemProp);
          aggregatedValues[mapping.cartProp] = maxTier;
        }

        // Boolean-arvot: strictest wins (false voittaa)
        for (const mapping of BOOLEAN_PROPERTY_MAP) {
          const strictest = computeStrictestBoolean(cart, mapping.prop);
          aggregatedValues[mapping.cartProp] = String(strictest);
        }

        // Needs-arvot: any true wins (true voittaa)
        for (const mapping of NEEDS_PROPERTY_MAP) {
          const anyTrue = computeAnyTrue(cart, mapping.prop);
          aggregatedValues[mapping.cartProp] = String(anyTrue);
        }

        // Päivitä jokainen line item
        for (const item of cart.items) {
          const currentProps = item.properties || {};
          const nextProps = { ...currentProps };
          // V3.3: Rikastetut item-propertyt ovat vasta muistissa (currentProps),
          // eivät vielä Shopifyssa — rivi pitää kirjoittaa joka tapauksessa
          let changed = enrichedKeys.has(item.key);

          // Cart-tier propertyt
          for (const mapping of TIER_PROPERTY_MAP) {
            const desired = aggregatedValues[mapping.cartProp];
            if (nextProps[mapping.cartProp] !== desired) {
              nextProps[mapping.cartProp] = desired;
              changed = true;
            }
          }

          // Cart-boolean propertyt
          for (const mapping of BOOLEAN_PROPERTY_MAP) {
            const desired = aggregatedValues[mapping.cartProp];
            if (nextProps[mapping.cartProp] !== desired) {
              nextProps[mapping.cartProp] = desired;
              changed = true;
            }
          }

          // Cart-needs propertyt
          for (const mapping of NEEDS_PROPERTY_MAP) {
            const desired = aggregatedValues[mapping.cartProp];
            if (nextProps[mapping.cartProp] !== desired) {
              nextProps[mapping.cartProp] = desired;
              changed = true;
            }
          }

          if (changed) {
            await updateLineItemProperties(item.key, nextProps, item.quantity);
          }
        }
      } catch (error) {
        console.error('[ShippingSync] Error syncing cart tiers:', error);
      }
    })();

    try {
      await syncInFlight;
    } finally {
      syncInFlight = null;
    }
  }

  /**
   * Debounced sync - odottaa 100ms ennen suoritusta
   */
  let syncTimeout = null;
  function debouncedSync() {
    if (syncTimeout) {
      clearTimeout(syncTimeout);
    }
    syncTimeout = setTimeout(() => {
      syncCartTiers();
    }, 100);
  }

  // ============================================================
  // EVENT LISTENERS
  // ============================================================

  /**
   * Kuuntele cart-muutoksia
   */
  function bindCartEvents() {
    // Theme events
    document.addEventListener('on:cart:add', debouncedSync);
    document.addEventListener('on:line-item:change', debouncedSync);
    
    // Cart drawer events
    document.addEventListener('dispatch:cart-drawer:refresh', debouncedSync);
    
    // Custom event jota voi kutsua mistä tahansa
    document.addEventListener('shipping:sync-cart-tiers', syncCartTiers);
  }

  /**
   * Hae checkout-URL elementistä tai sivulta (ei koskaan suhteellista /checkout,
   * jotta vältetään 301-uudelleenohjaukset toiselle domainille)
   */
  function getCheckoutUrlFromElement(el) {
    if (el.tagName === 'A' && el.href) {
      return el.href;
    }
    // Jos napilla on name="checkout", se on Shopify checkout-nappi
    // Ohjataan /checkout -osoitteeseen (ei lomakkeen action-osoitteeseen, joka voi olla /cart)
    if ((el.tagName === 'BUTTON' || el.tagName === 'INPUT') && el.getAttribute('name') === 'checkout') {
      const form = el.closest('form');
      if (form && form.action && form.action.includes('/checkout')) {
        return form.action;
      }
      // Lomakkeen action ei ole checkout (esim. /cart), käytetään /checkout
      return window.location.origin + '/checkout';
    }
    const form = el.closest('form');
    if (form && form.action && form.action.includes('/checkout')) {
      return form.action;
    }
    // Käytä sivulla olevaa checkout-linkkiä (täysi URL, ei 301)
    const pageCheckoutLink = document.querySelector('a[href*="checkout"]');
    return pageCheckoutLink ? pageCheckoutLink.href : null;
  }

  /**
   * Interceptoi checkout-napit varmistaakseen synkronoinnin
   */
  function interceptCheckoutButtons() {
    document.addEventListener('click', async (e) => {
      // Checkout-napit
      const checkoutBtn = e.target.closest('button[name="checkout"], a[href*="/checkout"], input[name="checkout"]');
      if (!checkoutBtn) return;

      // Älä interceptoi jos nappi on jo disabloitu
      if (checkoutBtn.disabled || checkoutBtn.getAttribute('aria-disabled') === 'true') {
        return;
      }

      const checkoutUrl = getCheckoutUrlFromElement(checkoutBtn);
      if (!checkoutUrl) {
        return; // Ei ohjata suhteellisella /checkout (301), jätetään oletustoiminta
      }

      e.preventDefault();
      e.stopPropagation();

      // Disabloi nappi
      checkoutBtn.setAttribute('aria-disabled', 'true');
      if (checkoutBtn.tagName === 'BUTTON' || checkoutBtn.tagName === 'INPUT') {
        checkoutBtn.disabled = true;
      }

      try {
        // Synkronoi ensin
        await syncCartTiers();
        // Ohjaa täydellä checkout-URL:lla (vältä 301)
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('[ShippingSync] Checkout intercept error:', error);
        checkoutBtn.removeAttribute('aria-disabled');
        if (checkoutBtn.tagName === 'BUTTON' || checkoutBtn.tagName === 'INPUT') {
          checkoutBtn.disabled = false;
        }
        window.location.href = checkoutUrl;
      }
    }, true); // Capture phase
  }

  /**
   * Hae korissa olevan variantin line item ja sen propertyt
   */
  async function getExistingCartItem(variantId) {
    try {
      const cart = await fetchCart();
      const variantIdStr = String(variantId);

      for (const item of cart.items || []) {
        if (String(item.variant_id) === variantIdStr) {
          return item;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Kasvata olemassa olevan line itemin määrää
   * Sisältää sections-parametrin jotta cart drawer päivittyy
   */
  async function increaseExistingItemQuantity(item, addQuantity, sectionsParam) {
    const newQuantity = item.quantity + addQuantity;

    const body = {
      id: item.key,
      quantity: newQuantity
    };

    // Lisää sections jos annettu (cart drawer päivitystä varten)
    if (sectionsParam) {
      body.sections = sectionsParam;
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error('Failed to update quantity');
    return response.json();
  }

  /**
   * Poimi quantity payloadista
   */
  function extractQuantityFromPayload(body) {
    if (!body) return 1;

    try {
      let data;
      if (typeof body === 'string') {
        data = JSON.parse(body);
      } else if (body instanceof FormData) {
        const qty = body.get('quantity');
        return qty ? parseInt(qty, 10) : 1;
      } else {
        data = body;
      }

      return data.quantity ? parseInt(data.quantity, 10) : 1;
    } catch (e) {
      return 1;
    }
  }

  /**
   * Poimi sections payloadista (cart drawer päivitystä varten)
   */
  function extractSectionsFromPayload(body) {
    if (!body) return null;

    try {
      let data;
      if (typeof body === 'string') {
        data = JSON.parse(body);
      } else if (body instanceof FormData) {
        return body.get('sections') || null;
      } else {
        data = body;
      }

      return data.sections || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Interceptoi cart/add -kutsut lisätäkseen propertyt JOS ne puuttuvat
   * V3.2: Jos sama variant on jo korissa, kasvata määrää sen sijaan että luot uuden rivin
   */
  function interceptCartAdd() {
    const originalFetch = window.fetch;

    window.fetch = async function(url, options = {}) {
      // Tarkista onko cart/add kutsu
      if (typeof url === 'string' && url.includes('/cart/add') && options.method === 'POST') {
        let body = options.body;
        let modified = false;

        const variantId = extractVariantIdFromPayload(body);

        if (variantId) {
          // V3.2: Tarkista onko sama variant jo korissa
          const existingItem = await getExistingCartItem(variantId);

          if (existingItem) {
            // Variant on jo korissa - kasvata määrää sen sijaan
            const addQuantity = extractQuantityFromPayload(body);
            const sections = extractSectionsFromPayload(body);

            try {
              const result = await increaseExistingItemQuantity(existingItem, addQuantity, sections);

              // Synkronoi cart tiers
              setTimeout(debouncedSync, 200);

              // Palauta "fake" response joka näyttää onnistuneelta cart/add:ltä
              // Tämä on tarpeen jotta product-form.js käsittelee vastauksen oikein
              return new Response(JSON.stringify({
                ...result,
                variant_id: parseInt(variantId, 10),
                quantity: addQuantity,
                items: result.items
              }), {
                status: 200,
                headers: { 'Content-Type': 'application/json' }
              });
            } catch (e) {
              // Jos epäonnistuu, jatka normaalilla cart/add:llä
              console.warn('[ShippingSync] Failed to increase quantity, falling back to cart/add:', e);
            }
          }

          // Tarkista onko propertyt jo mukana
          if (!hasShippingProperties(body)) {
            const shippingData = getVariantShippingData(variantId);
            const shippingProps = createShippingPropertiesFromData(shippingData);

            if (shippingProps) {
              // Lisää myös cart_tier propertyt heti jos korissa on jo muita tuotteita
              const existingCart = await fetchCart().catch(() => null);
              if (existingCart && existingCart.items && existingCart.items.length > 0) {
                // Laske aggregoidut arvot nykyisestä korista + uusi tuote
                for (const mapping of TIER_PROPERTY_MAP) {
                  const currentMax = computeMaxTier(existingCart, mapping.itemProp);
                  shippingProps[mapping.cartProp] = currentMax;
                }
                for (const mapping of BOOLEAN_PROPERTY_MAP) {
                  const strictest = computeStrictestBoolean(existingCart, mapping.prop);
                  shippingProps[mapping.cartProp] = String(strictest);
                }
                for (const mapping of NEEDS_PROPERTY_MAP) {
                  const anyTrue = computeAnyTrue(existingCart, mapping.prop);
                  shippingProps[mapping.cartProp] = String(anyTrue);
                }
              }

              // JSON body
              if (typeof body === 'string') {
                options = { ...options, body: addPropertiesToJsonPayload(body, shippingProps) };
                modified = true;
              }
              // FormData body
              else if (body instanceof FormData) {
                addPropertiesToFormData(body, shippingProps);
                modified = true;
              }
            }
          }
        }

        // Kutsu alkuperäinen fetch
        const response = await originalFetch.call(this, url, modified ? options : arguments[1]);

        // Synkronoi JÄLKEEN lisäyksen (aggregoi cart tiers)
        setTimeout(debouncedSync, 200);

        return response;
      }

      // Cart change / update - synkronoi jälkeen
      if (typeof url === 'string' && (url.includes('/cart/change') || url.includes('/cart/update'))) {
        const response = await originalFetch.apply(this, arguments);
        setTimeout(debouncedSync, 200);
        return response;
      }

      return originalFetch.apply(this, arguments);
    };
    
    // Interceptoi myös XMLHttpRequest (backup)
    const originalXHROpen = XMLHttpRequest.prototype.open;
    const originalXHRSend = XMLHttpRequest.prototype.send;
    
    XMLHttpRequest.prototype.open = function(method, url) {
      this._shippingSyncUrl = url;
      this._shippingSyncMethod = method;
      return originalXHROpen.apply(this, arguments);
    };
    
    XMLHttpRequest.prototype.send = function(body) {
      const url = this._shippingSyncUrl;
      
      // Jos cart/add tai cart/change, synkronoi jälkeen
      if (url && (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'))) {
        this.addEventListener('load', () => {
          setTimeout(debouncedSync, 200);
        });
      } 
      
      return originalXHRSend.apply(this, arguments);
    };
  }

  // ============================================================
  // ALUSTUS
  // ============================================================

  function init() {
    bindCartEvents();
    interceptCheckoutButtons();
    interceptCartAdd();
    
    // Synkronoi heti sivun latautuessa
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(debouncedSync, 500);
      });
    } else {
      setTimeout(debouncedSync, 500);
    }
  }

  // Julkinen API
  window.HappyAnglerShipping = {
    syncCartTiers: syncCartTiers,
    debouncedSync: debouncedSync,
    getVariantShippingData: getVariantShippingData,
  };

  init();
})();